import React, { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import Navigation from "./Navigation";
import Coin from "./Coin";
import { t } from "../i18n";
import { useSounds } from "../sound";
import { pendingTokens, pendingTotals, clearPendingRewards } from "../pendingRewards";
import PageHead from "./PageHead";
import { useFeatures } from "../features";

// Dans l'appli (WebView locale), une redirection plein écran quitte le bundle
// et finit sur Safari sans retour possible : le flux s'ouvre alors dans un
// navigateur embarqué, et le serveur est prévenu pour renvoyer vers le schéma
// custom de l'appli plutôt que vers le site web à la fin.
function startSso(api) {
  const url = `${api}/auth/sso/start${Capacitor.isNativePlatform() ? "?client=app" : ""}`;
  if (Capacitor.isNativePlatform()) Browser.open({ url });
  else window.location.href = url;
}

const API = process.env.REACT_APP_API_URL || "";

// Login / sign-up form (tabbed). Self-contained: calls the /auth/login|register
// endpoints and hands the account back through onSuccess(account, mode).
// Sign-up always creates a "user" role server-side.
function Auth({ initialMode = "login", onSuccess, onBack }) {
  const { playSuccess } = useSounds();
  const features = useFeatures();
  const [mode, setMode] = useState(initialMode); // "login" | "register" | "forgot"
  // Arrivé sur l'onglet d'inscription alors qu'elles sont fermées : retour à la connexion.
  useEffect(() => { if (!features.register && mode === "register") setMode("login"); }, [features.register, mode]);
  const pending = pendingTotals();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Mot de passe oublié : pas de mail sur une instance auto-hébergée, la
  // demande part au superadmin — la réponse ne confirme jamais si le compte existe.
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);

  // Le bouton SSO n'apparaît que si le superadmin a configuré un fournisseur.
  const [sso, setSso] = useState(null); // { enabled, label }
  useEffect(() => {
    fetch(`${API}/auth/sso/config`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setSso(d))
      .catch(() => {});
  }, []);

  const switchMode = (m) => { setMode(m); setError(""); setForgotMsg(""); };

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotBusy(true);
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: forgotUsername.trim() }),
      });
      const data = await res.json();
      setForgotMsg(data.message || data.error || t("common.error"));
    } catch (_) {
      setForgotMsg(t("common.serverUnreachable"));
    }
    setForgotBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${API}/auth/${mode}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Les gains accumulés en anonyme voyagent avec l'inscription
        body: JSON.stringify({
          username: username.trim(), password,
          ...(mode === "register" ? { claims: pendingTokens() } : {}),
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || t("common.error")); setBusy(false); return; }
      playSuccess();
      if (mode === "register") clearPendingRewards();
      onSuccess({ token: data.token, username: data.username, role: data.role, pseudo: data.pseudo || null, picture: data.picture || null, claimed: data.claimed }, mode);
    } catch (_) {
      setError(t("common.serverUnreachable"));
      setBusy(false);
    }
  };

  return (
    <div class="container">
      <Navigation />
      <PageHead onBack={onBack} />
      <div class="Home">
        <div class="centered-content">
          {mode === "forgot" ? (
            <form class="auth-form" onSubmit={submitForgot}>
              <p class="account-hint">{t("auth.forgotHint")}</p>
              <input
                type="text" placeholder={t("auth.username")} value={forgotUsername} autoComplete="username" autoFocus
                onChange={e => setForgotUsername(e.target.value)}
              />
              {forgotMsg && <p class="account-hint">{forgotMsg}</p>}
              <div class="btn-group vertical">
                <button class="btn btn-primary" type="submit" disabled={!forgotUsername.trim() || forgotBusy}>
                  {t("auth.forgotSubmit")}
                </button>
                <button class="btn" type="button" onClick={() => switchMode("login")}>{t("auth.forgotBack")}</button>
              </div>
            </form>
          ) : (<>
          <div class="auth-tabs">
            <button type="button" class={`auth-tab${mode === "login" ? " active" : ""}`} onClick={() => switchMode("login")}>{t("auth.login")}</button>
            {/* Inscriptions fermées par l'administration : seul l'onglet de connexion reste. */}
            {features.register && (
            <button type="button" class={`auth-tab${mode === "register" ? " active" : ""}`} onClick={() => switchMode("register")}>{t("auth.register")}</button>
            )}
          </div>
          <form class="auth-form" onSubmit={submit}>
            <input
              type="text" placeholder={t("auth.username")} value={username} autoComplete="username" autoFocus
              class={error ? "is-error" : undefined} aria-invalid={error ? "true" : undefined}
              onChange={e => setUsername(e.target.value)}
            />
            <input
              type="password"
              placeholder={mode === "register" ? t("auth.passwordMin") : t("auth.password")}
              value={password}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              class={error ? "is-error" : undefined} aria-invalid={error ? "true" : undefined}
              onChange={e => setPassword(e.target.value)}
            />
            {mode === "register" && pending.coins + pending.junk > 0 && (
              <p class="auth-pending">
                <Coin size={17} /> {t("auth.pendingRewards", { coins: pending.coins })}
                {pending.junk > 0 && <><Coin kind="junk" size={17} /> {pending.junk}</>}
              </p>
            )}
            {mode === "register" && (
              <p class="account-hint">{t("auth.registerHint")}</p>
            )}
            {error && <p class="auth-error">{error}</p>}
            <div class="btn-group vertical">
              <button class="btn btn-primary" type="submit" disabled={!username.trim() || !password || busy}>
                {mode === "login" ? t("auth.submitLogin") : t("auth.submitRegister")}
              </button>
            </div>
            {sso?.enabled && (
              <>
                <span class="auth-separator">{t("auth.or")}</span>
                <button type="button" class="btn auth-sso-btn" onClick={() => startSso(API)}>
                  {t("auth.ssoSubmit", { provider: sso.label })}
                </button>
              </>
            )}
            {mode === "login" && (
              <button type="button" class="auth-forgot-link" onClick={() => switchMode("forgot")}>{t("auth.forgotPassword")}</button>
            )}
          </form>
          </>)}
        </div>
      </div>
    </div>
  );
}

export default Auth;
